<template>
  <div :class="['content mb-5', { 'policy-no-subtitle': !pageMeta.subTitle }]">
    <b-row>
      <b-col>
        <h2 class="mb-3">{{ $t("Device Exception Rules") }}</h2>
        <t-data-table
          data-test-id="device-exceptions-rules-data-table"
          with-filter
          hoverable
          :filter-placeholder="$t('Search Device Exception Rules')"
          :loading="loading"
          :table-data="tableData"
          :actions="tableActions"
          @on-delete="onDelete"
          @on-add="onAdd"
          @on-export="onExport"
          @on-row-clicked="onRowClicked"
          :searchExports="searchExports"
          @export-headers="exportedHeaders"
          :preventDeleteLast="preventDeleteLast"
          :preventDeleteLastString="
            $t(
              'This device exception is in use, so must have at least one rule. Create another rule before deleting this one, or delete the device exception if it is no longer in use.'
            )
          "
        />
      </b-col>
    </b-row>
  </div>
</template>

<route>
{
"name": "Device Exception Rules",
"meta": {
"authorize" : [1, 3, 4]
}
}
</route>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";

export default {
  layout: "customer",
  methods: {
    ...mapActions("pageMeta", ["setPageTitle", "setPageSubtitle"]),
    ...mapActions("toast", ["displayToast"]),
    ...mapActions("deviceExceptions", [
      "fetchDeviceExceptionRules",
      "deleteDeviceExceptionRules",
    ]),
    ...mapMutations("modal", ["createModal", "showModal"]),
    forceUpdate() {
      this.$forceUpdate();
    },
    onAdd() {
      this.createModal({
        modal: "t-store-device-exception-rules",
        dataTestId: "modal-store-device-exception-rules",
        title: this.$t("Add Device Exception Rule"),
        modalData: {
          createNew: true,
          exceptionId: this.exceptionId,
        },
        props: {
          size: "lg",
          okTitle: this.$t("Add"),
        },
      });
      this.showModal();
    },
    async onDelete(rules) {
      try {
        await this.deleteDeviceExceptionRules({
          exceptionId: this.exceptionId,
          rules,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Deleted X X", {
            count: rules.length,
            item: this.$t("Exception Rule(s)"),
          }),
          duration: 2000,
          variant: "success",
        });
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      }
    },
    onRowClicked(item) {
      this.createModal({
        modal: "t-store-device-exception-rules",
        dataTestId: "modal-store-device-exception-rules",
        title: this.$t("Update Device Exception Rule"),
        modalData: {
          createNew: false,
          exceptionId: this.exceptionId,
          item: item,
        },
        props: {
          size: "lg",
          okTitle: this.$t("Save"),
        },
      });
      this.showModal();
    },
    exportedHeaders(headers) {
      this.currentHeaders = headers;
    },
    onExport(items) {
      if (items.length) {
        const exportData = items.map((res) => {
          let payload = {};
          this.currentHeaders.forEach((headers) => {
            return (payload[headers.value] = res[headers.value]);
          });

          return payload;
        });
        this.$downloadCSV(exportData, "Device Exception Rules");
      } else {
        this.displayToast({
          title: this.$t("Failure"),
          message: this.$t("No Device Exception Rules in table"),
          duration: 2000,
          variant: "danger",
        });
      }
    },
  },
  data: function () {
    return {
      exceptionName: null,
      exceptionId: null,
      preventDeleteLast: false,
      loading: true,
      currentHeaders: null,
      tableActions: {
        add: {
          enabled: true,
          actions: [
            {
              text: this.$t("Add Device Exception Rule"),
            },
          ],
        },
        export: { enabled: true },
        import: { enabled: false },
        delete: { enabled: true },
      },
      searchExports: ["name", "status", "user", "key"],
    };
  },
  computed: {
    ...mapGetters("authentication", ["selectedUser"]),
    ...mapGetters("deviceExceptions", ["deviceExceptionRules"]),
    ...mapGetters("pageMeta", ["pageMeta"]),
    tableData: {
      get() {
        return this.$store.state.deviceExceptions.deviceExceptionRulesData;
      },
      set() {},
    },
  },
  watchQuery: [
    "exceptionId",
    "exceptionName",
    "exceptionIsDefault",
    "exceptionDevicesCount",
  ],
  async created() {
    this.exceptionId = this.$route.query.exceptionId || 1;
    this.exceptionName =
      this.$route.query.exceptionName || "Selected exception name placeholder";

    this.preventDeleteLast =
      this.$route.query.exceptionIsDefault === "true" ||
      this.$route.query.exceptionDevicesCount > 0;

    this.setPageTitle(this.exceptionName);
    this.setPageSubtitle([
      this.$t(
        "A rule is an adjustment to a device. One or many rules can be added to a device exception, which can then be applied to a device. Rule types: 1.ByPass OTG: Do not use OTG when at a particular IP and/or when accessing a particular domain; 2.Use Specific Resolver: Use a specific resolver when at a particular IP and/or when accessing a particular domain."
      ),
    ]);

    await this.fetchDeviceExceptionRules({
      userId: this.selectedUser.id,
      exceptionId: this.exceptionId,
    });

    this.loading = false;
  },
};
</script>
